import type { PaletteMode } from '@mui/material';

import * as React from 'react';
import { baseThemeOptions, darkThemeOptions, lightThemeOptions } from '@mend/mui';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';

const ThemeModeContext = React.createContext<{
  toggle: (to?: PaletteMode) => void;
} | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useThemeMode() {
  const context = React.useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeModeProvider');
  }
  return context;
}

export function ThemeModeProvider(props: { children: React.ReactNode }) {
  const [mode, setMode] = React.useState<PaletteMode>('dark');

  const value = React.useMemo(
    () => ({
      toggle: (to?: PaletteMode) => setMode((p) => to ?? (p === 'light' ? 'dark' : 'light')),
    }),
    []
  );

  const theme = React.useMemo(
    () =>
      createTheme(
        deepmerge(baseThemeOptions, mode === 'light' ? lightThemeOptions : darkThemeOptions)
      ),
    [mode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>{props.children}</MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
}
